import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Radio } from 'antd';
import type { DictionaryType } from './dictManageData';

export type DictTypeFormValues = {
  name: string;
  code: string;
  description?: string;
  visibility: 'PUBLIC' | 'ACCOUNT';
  status: 'ACTIVE' | 'DISABLED';
};

type DictTypeModalProps = {
  open: boolean;
  editing?: DictionaryType | null;
  onClose: () => void;
  onSubmit: (values: DictTypeFormValues) => Promise<boolean> | boolean;
};

const CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,63}$/;

export function DictTypeModal({ open, editing, onClose, onSubmit }: DictTypeModalProps) {
  const [form] = Form.useForm<DictTypeFormValues>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const status = editing?.status === 'DISABLED' ? 'DISABLED' : 'ACTIVE';
    const visibility = editing?.visibility === 'ACCOUNT' ? 'ACCOUNT' : 'PUBLIC';
    form.setFieldsValue({
      name: editing?.name,
      code: editing?.code,
      description: editing?.description,
      status,
      visibility
    });
  }, [open, editing, form]);

  const submit = async () => {
    const values = await form.validateFields();
    const description = values.description?.trim() ?? '';
    setSubmitting(true);
    try {
      await onSubmit({
        name: values.name.trim(),
        code: values.code.trim().toUpperCase(),
        description,
        visibility: values.visibility,
        status: values.status
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={editing ? '编辑字典类型' : '新增字典类型'}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={520}
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose}>
            取消
          </Button>
          <Button type='primary' className='wb-btn-primary rounded-lg' loading={submitting} onClick={submit}>
            确定
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout='horizontal'
        labelCol={{ flex: '100px' }}
        wrapperCol={{ flex: 1 }}
        labelAlign='right'
        requiredMark
        className='admin-dict-type-form pt-1'
        initialValues={{ status: 'ACTIVE', visibility: 'PUBLIC' }}
      >
        <Form.Item
          name='name'
          label='字典名称'
          rules={[
            { required: true, whitespace: true, message: '请输入字典名称' },
            { max: 128, message: '字典名称最多 128 个字符' }
          ]}
        >
          <Input placeholder='如：语言' maxLength={128} />
        </Form.Item>
        <Form.Item
          name='code'
          label='字典标识'
          getValueFromEvent={(event) => event.target.value.toUpperCase()}
          rules={[
            { required: true, whitespace: true, message: '请输入字典标识' },
            {
              pattern: CODE_PATTERN,
              message: '大写字母开头，仅含大写字母、数字或下划线，2-64 位'
            }
          ]}
        >
          <Input placeholder='如：LOCALE' disabled={!!editing} maxLength={64} />
        </Form.Item>
        <Form.Item name='visibility' label='可见范围'>
          <Radio.Group>
            <Radio value='PUBLIC'>无需登录</Radio>
            <Radio value='ACCOUNT'>登录可读</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name='status' label='状态' rules={[{ required: true, message: '请选择状态' }]}>
          <Radio.Group>
            <Radio value='ACTIVE'>启用</Radio>
            <Radio value='DISABLED'>停用</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name='description'
          label='说明'
          rules={[{ max: 500, message: '说明最多 500 个字符' }]}
        >
          <Input.TextArea rows={3} placeholder='请输入说明' maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
